import moment from 'moment';

function resolveMoment(value, fallback) {
  if (moment.isMoment(value) && value.isValid()) return value;
  if (typeof value === 'string' && value) {
    const parsed = moment(value);
    if (parsed.isValid()) return parsed;
  }
  return fallback;
}

function computeNights(roomCheckIn, roomCheckOut, bookingType) {
  const nights =
    bookingType === 'nightly'
      ? Math.max(
          0,
          roomCheckOut.clone().startOf('day').diff(roomCheckIn.clone().startOf('day'), 'days')
        )
      : roomCheckOut.diff(roomCheckIn, 'days');
  return nights <= 0 ? 1 : nights;
}

function computeExtraCharges(room, nightsToUse, isWalkIn) {
  const extraAdultRate = room.extraAdultRate || 0;
  const extraChildRate = room.extraChildRate || 0;
  const extraAdultsCount = room.extraAdultsCount || 0;
  const extraChildrenCount = room.extraChildrenCount || 0;

  const extraAdultCharge = isWalkIn
    ? extraAdultRate * extraAdultsCount * nightsToUse
    : room.extraAdultCharge || 0;
  const extraChildCharge = isWalkIn
    ? extraChildRate * extraChildrenCount * nightsToUse
    : room.extraChildCharge || 0;

  return {
    extraAdultRate,
    extraChildRate,
    extraAdultsCount,
    extraChildrenCount,
    extraAdultCharge,
    extraChildCharge,
    hasExtraCharges: extraAdultCharge > 0 || extraChildCharge > 0,
  };
}

function computeLineAmount(room, nightsToUse, isWalkIn, extraCharges) {
  if (isWalkIn && room.ratePerNight && nightsToUse) {
    const basePrice = (room.ratePerNight || 0) * nightsToUse;
    return basePrice + extraCharges.extraAdultCharge + extraCharges.extraChildCharge;
  }
  return room.total || room.priceAfterDiscount || 0;
}

function roundCurrency(value) {
  return Math.round((Number(value) || 0) * 100) / 100;
}

/** Normalized invoice row data for desktop table and mobile cards. */
export function buildRoomInvoiceLines(
  rooms,
  {
    checkInDate,
    checkOutDate,
    nights,
    isWalkIn,
    bookingType,
    priceBreakdown,
    gstIncluded,
    taxInclusive,
    hasTaxRules,
  }
) {
  const gstActive = gstIncluded && hasTaxRules;
  return (rooms || []).map((room, index) => {
    let roomCheckIn = checkInDate;
    let roomCheckOut = checkOutDate;
    let roomNights = 1;

    if (isWalkIn) {
      roomNights = nights || 1;
    } else {
      roomCheckIn = resolveMoment(room.checkInDate, checkInDate);
      roomCheckOut = resolveMoment(room.checkOutDate, checkOutDate);
      roomNights = computeNights(roomCheckIn, roomCheckOut, bookingType);
    }

    const nightsToUse = isWalkIn ? nights || 1 : room.days || roomNights;
    const gstLine = priceBreakdown?.gstLineBreakdown?.[index] || {};
    const rowGstAmount = gstActive ? Number(gstLine.gstAmount || 0) : 0;
    const rowGstPercentage = gstActive ? Number(gstLine.gstPercentage || 0) : 0;
    const extraCharges = computeExtraCharges(room, nightsToUse, isWalkIn);
    const grossLineAmount = computeLineAmount(room, nightsToUse, isWalkIn, extraCharges);
    const rowNetAmount = gstActive && taxInclusive
      ? Number(gstLine.netAmount || 0)
      : grossLineAmount;
    const lineAmount = gstActive && taxInclusive
      ? roundCurrency(rowNetAmount + rowGstAmount)
      : grossLineAmount;
    const netRatio = gstActive && taxInclusive && grossLineAmount > 0
      ? rowNetAmount / grossLineAmount
      : 1;
    const lineQuantity = Math.max(1, room.rooms || 1) * nightsToUse;
    const displayRatePerNight = gstActive && taxInclusive && lineQuantity > 0
      ? roundCurrency(rowNetAmount / lineQuantity)
      : (room.ratePerNight
        ? roundCurrency((room.ratePerNight || 0) * netRatio)
        : (lineQuantity > 0 ? roundCurrency(rowNetAmount / lineQuantity) : 0));
    const displayExtraAdultRate = roundCurrency((extraCharges.extraAdultRate || 0) * netRatio);
    const displayExtraChildRate = roundCurrency((extraCharges.extraChildRate || 0) * netRatio);
    const displayExtraAdultCharge = roundCurrency((extraCharges.extraAdultCharge || 0) * netRatio);
    const displayExtraChildCharge = roundCurrency((extraCharges.extraChildCharge || 0) * netRatio);
    const unitPrice = lineQuantity > 0 ? roundCurrency(rowNetAmount / lineQuantity) : 0;
    const roomNumber = room.roomNumber || room.room_number;

    return {
      room,
      index,
      roomCheckIn,
      roomCheckOut,
      nightsToUse,
      unitPrice,
      displayRatePerNight,
      rowNetAmount,
      grossLineAmount,
      taxInclusive: gstActive && taxInclusive,
      rowGstAmount,
      rowGstPercentage,
      lineAmount,
      roomNumber,
      ...extraCharges,
      displayExtraAdultRate,
      displayExtraChildRate,
      displayExtraAdultCharge,
      displayExtraChildCharge,
    };
  });
}

export function formatReviewStayDate(date) {
  return moment.isMoment(date) ? date.format('DD MMM YYYY') : moment(date).format('DD MMM YYYY');
}
